"use client";

import { useRouter } from "next/navigation";
import { useState, useEffect, useTransition, useMemo, useRef, useCallback } from "react";
import { format, parse } from "date-fns";
import { DatePicker } from "./date-picker";
import { Button } from "@/components/ui/button";
import { useLoginGate } from "@/components/login-gate-dialog";
import { useAuth } from "@/components/auth-provider";
import { AliyunCaptcha, type AliyunCaptchaHandle, type CaptchaVerifyOutcome } from "@/components/aliyun-captcha";
import {
  Calendar,
  RotateCcw,
  Loader2,
  ChevronDown,
} from "lucide-react";
import { formatBeijingDateTime, formatBeijingTime, formatDateParam, parseTimeOfDay } from "@/lib/hotlist-time";
import { trackTimeTravel, claritySetTag } from "@/lib/analytics";

// 阿里云验证码配置（与注册/找回密码共用同一 sceneId）
const CAPTCHA_PREFIX = process.env.NEXT_PUBLIC_ALIYUN_CAPTCHA_PREFIX;
const CAPTCHA_SCENE_ID = process.env.NEXT_PUBLIC_ALIYUN_CAPTCHA_SCENE_ID;
const CAPTCHA_CONFIGURED = !!(CAPTCHA_PREFIX && CAPTCHA_SCENE_ID);

const PASS_STORAGE_KEY = "history_pass";
const LOGIN_REQUIRED_MONTHS = 3;

interface StoredPass {
  token: string;
  expiresAt: number;
}

function loadPass(): StoredPass | null {
  try {
    const raw = sessionStorage.getItem(PASS_STORAGE_KEY);
    if (!raw) return null;
    const pass = JSON.parse(raw) as StoredPass;
    if (Date.now() >= pass.expiresAt) {
      sessionStorage.removeItem(PASS_STORAGE_KEY);
      return null;
    }
    return pass;
  } catch {
    return null;
  }
}

function savePass(pass: StoredPass) {
  try {
    sessionStorage.setItem(PASS_STORAGE_KEY, JSON.stringify(pass));
  } catch {
    // sessionStorage 不可用时静默忽略
  }
}

interface HotlistHeaderProps {
  currentTime: Date | null;
  selectedTimeId?: number;
  requestedAt?: string;
  matchedAt?: string;
  historyDate?: string;
  minDate?: Date;
  maxDate?: Date;
}

function formatMinuteOfDay(minuteOfDay: number) {
  const hour = Math.floor(minuteOfDay / 60);
  const minute = minuteOfDay % 60;
  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
}

function minuteOfDayFromDate(date: Date | null) {
  if (!date) return 12 * 60;
  const beijingTime = formatBeijingTime(date);
  return parseTimeOfDay(beijingTime) ?? 12 * 60;
}

function formatSnapshotText(currentTime: Date | null, selectedDate?: Date) {
  if (!currentTime) return "加载中...";
  const prefix = selectedDate ? "历史现场" : "实时";
  return `${prefix} · ${formatBeijingDateTime(new Date(currentTime))}`;
}

function getTimePeriod(minute: number) {
  const hour = Math.floor(minute / 60);
  if (hour < 5) {
    return {
      label: "深夜",
      cue: "低噪声观察",
      accent: "#9f7aea",
      sky: "linear-gradient(160deg, #050816 0%, #0b1630 48%, #1b1740 100%)",
      horizon: "linear-gradient(0deg, rgba(91,33,182,0.36), rgba(91,33,182,0))",
      orb: "#f8fafc",
      orbCore: "#dbeafe",
      orbGlow: "rgba(191,219,254,0.62)",
      mist: "rgba(59,130,246,0.14)",
      starsOpacity: 0.9,
      cloudsOpacity: 0.08,
    };
  }
  if (hour < 7) {
    return {
      label: "黎明",
      cue: "早间升温",
      accent: "#fb923c",
      sky: "linear-gradient(160deg, #25305f 0%, #d96a5b 54%, #ffd89a 100%)",
      horizon: "linear-gradient(0deg, rgba(251,146,60,0.64), rgba(251,146,60,0))",
      orb: "#fff7ed",
      orbCore: "#fed7aa",
      orbGlow: "rgba(251,146,60,0.74)",
      mist: "rgba(255,237,213,0.24)",
      starsOpacity: 0.25,
      cloudsOpacity: 0.34,
    };
  }
  if (hour < 12) {
    return {
      label: hour < 9 ? "清晨" : "上午",
      cue: hour < 9 ? "通勤热度" : "议程展开",
      accent: "#f59e0b",
      sky: "linear-gradient(160deg, #7dd3fc 0%, #dbeafe 52%, #fff1b8 100%)",
      horizon: "linear-gradient(0deg, rgba(250,204,21,0.52), rgba(56,189,248,0))",
      orb: "#fff7c2",
      orbCore: "#facc15",
      orbGlow: "rgba(250,204,21,0.72)",
      mist: "rgba(255,255,255,0.28)",
      starsOpacity: 0,
      cloudsOpacity: 0.46,
    };
  }
  if (hour < 17) {
    return {
      label: hour < 14 ? "中午" : "下午",
      cue: hour < 14 ? "峰值观察" : "持续发酵",
      accent: "#38bdf8",
      sky: "linear-gradient(160deg, #38bdf8 0%, #93c5fd 44%, #fef3c7 100%)",
      horizon: "linear-gradient(0deg, rgba(14,165,233,0.34), rgba(14,165,233,0))",
      orb: "#fffbe6",
      orbCore: "#fde047",
      orbGlow: "rgba(253,224,71,0.7)",
      mist: "rgba(255,255,255,0.22)",
      starsOpacity: 0,
      cloudsOpacity: 0.38,
    };
  }
  if (hour < 19) {
    return {
      label: "傍晚",
      cue: "晚高峰切片",
      accent: "#fb7185",
      sky: "linear-gradient(160deg, #4f46e5 0%, #fb7185 46%, #fbbf24 100%)",
      horizon: "linear-gradient(0deg, rgba(251,113,133,0.66), rgba(251,113,133,0))",
      orb: "#fff7ed",
      orbCore: "#fb923c",
      orbGlow: "rgba(251,113,133,0.78)",
      mist: "rgba(251,191,36,0.18)",
      starsOpacity: 0.12,
      cloudsOpacity: 0.32,
    };
  }
  if (hour < 21) {
    return {
      label: "夜幕",
      cue: "黄金时段",
      accent: "#a78bfa",
      sky: "linear-gradient(160deg, #131a3c 0%, #3b2163 50%, #7c2d12 100%)",
      horizon: "linear-gradient(0deg, rgba(124,58,237,0.48), rgba(124,58,237,0))",
      orb: "#f8fafc",
      orbCore: "#c4b5fd",
      orbGlow: "rgba(167,139,250,0.68)",
      mist: "rgba(167,139,250,0.18)",
      starsOpacity: 0.52,
      cloudsOpacity: 0.18,
    };
  }
  return {
    label: "夜晚",
    cue: "长尾回看",
    accent: "#60a5fa",
    sky: "linear-gradient(160deg, #020617 0%, #0f1e3d 54%, #172554 100%)",
    horizon: "linear-gradient(0deg, rgba(37,99,235,0.36), rgba(37,99,235,0))",
    orb: "#f8fafc",
    orbCore: "#bfdbfe",
    orbGlow: "rgba(96,165,250,0.64)",
    mist: "rgba(96,165,250,0.16)",
    starsOpacity: 0.78,
    cloudsOpacity: 0.1,
  };
}

function isOlderThanLoginRequiredWindow(date: Date, now = new Date()) {
  const selected = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const cutoff = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  cutoff.setMonth(cutoff.getMonth() - LOGIN_REQUIRED_MONTHS);
  return selected < cutoff;
}

function isDateStringOlderThanLoginRequiredWindow(date: string) {
  const parsed = parse(date, "yyyy-MM-dd", new Date());
  return isOlderThanLoginRequiredWindow(parsed);
}

function clamp(value: number, min: number, max: number) {
  return Math.max(min, Math.min(max, value));
}

function smoothstep(edgeStart: number, edgeEnd: number, value: number) {
  if (edgeStart === edgeEnd) return value >= edgeEnd ? 1 : 0;
  const x = clamp((value - edgeStart) / (edgeEnd - edgeStart), 0, 1);
  return x * x * (3 - 2 * x);
}

function phaseWindow(minute: number, fadeInStart: number, fullStart: number, fullEnd: number, fadeOutEnd: number) {
  return smoothstep(fadeInStart, fullStart, minute) * (1 - smoothstep(fullEnd, fadeOutEnd, minute));
}

function getSceneVisuals(minute: number) {
  const dawn = phaseWindow(minute, 270, 360, 430, 560);
  const day = phaseWindow(minute, 390, 520, 960, 1090);
  const noon = phaseWindow(minute, 610, 720, 830, 940);
  const sunset = phaseWindow(minute, 960, 1080, 1150, 1260);
  const evening = phaseWindow(minute, 1120, 1220, 1340, 1439);
  const night = Math.max(1 - smoothstep(260, 430, minute), smoothstep(1180, 1320, minute));
  const moonMorning = 1 - smoothstep(260, 410, minute);
  const moonEvening = smoothstep(1120, 1260, minute);
  const moonOpacity = clamp(Math.max(moonMorning, moonEvening), 0, 1);
  const sunOpacity = smoothstep(300, 420, minute) * (1 - smoothstep(1050, 1170, minute));
  const sunProgress = clamp((minute - 300) / (14 * 60), 0, 1);
  const moonProgress = minute >= 19 * 60
    ? clamp((minute - 19 * 60) / (10 * 60), 0, 1)
    : clamp((minute + 5 * 60) / (10 * 60), 0, 1);
  const timeProgress = minute / (24 * 60 - 1);
  const horizonWarmth = clamp(dawn * 0.36 + noon * 0.16 + sunset * 0.5, 0, 0.7);
  const ambientGlow = clamp(0.16 + dawn * 0.18 + day * 0.16 + noon * 0.1 + sunset * 0.26 + night * 0.08, 0.16, 0.48);

  return {
    dawn,
    day,
    noon,
    sunset,
    evening,
    night,
    starsOpacity: clamp(night * 0.86 + evening * 0.24 + sunset * 0.08, 0, 0.9),
    cloudOpacity: clamp(0.08 + dawn * 0.18 + day * 0.24 + sunset * 0.16, 0.08, 0.42),
    mistOpacity: clamp(0.46 + dawn * 0.22 + sunset * 0.18 + night * 0.14, 0.42, 0.8),
    horizonWarmth,
    ambientGlow,
    timeProgress,
    starShift: -10 + timeProgress * 20,
    cloudShift: 18 - timeProgress * 36,
    lightFocus: 12 + timeProgress * 76,
    sunOpacity,
    sunLeft: 10 + sunProgress * 80,
    sunTop: 62 - Math.sin(sunProgress * Math.PI) * 42,
    moonOpacity,
    moonLeft: 10 + moonProgress * 80,
    moonTop: 31 + Math.sin(moonProgress * Math.PI) * 11,
  };
}

export function HotlistHeader({
  currentTime,
  selectedTimeId,
  requestedAt,
  matchedAt,
  historyDate,
  minDate,
  maxDate,
}: HotlistHeaderProps) {
  const router = useRouter();
  const effectiveDate = historyDate;
  const [isPending, startTransition] = useTransition();
  const [mintingToken, setMintingToken] = useState(false);
  const [pendingText, setPendingText] = useState("");
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const navigating = mintingToken || isPending;
  // 精确选择/快捷按钮默认折叠，避免历史页首屏被工具面板挤压。
  const [showMore, setShowMore] = useState(false);

  // 登录态（已登录用户直接用 JWT 凭证，无需验证码）
  const { user } = useAuth();

  // passToken：未登录用户无感验证后的会话凭证
  const [passToken, setPassToken] = useState<StoredPass | null>(() => loadPass());
  const captchaRef = useRef<AliyunCaptchaHandle>(null);

  // 等待验证码完成后要执行的导航动作
  const pendingNavigationRef = useRef<{ date: string; minute: number } | null>(null);

  const { requireLogin: requireHistoryLogin, dialog: historyLoginDialog } = useLoginGate({
    title: "登录后查看历史热搜",
    description: "为防止机器人批量抓取，3 个月以前的实时历史快照需要登录后查看。",
  });

  const [selectedDate, setSelectedDate] = useState<Date | undefined>(
    effectiveDate ? parse(effectiveDate, "yyyy-MM-dd", new Date()) : undefined
  );
  const [targetMinute, setTargetMinute] = useState(() => (
    parseTimeOfDay(requestedAt || null) ?? minuteOfDayFromDate(currentTime)
  ));
  const lastCommittedMinuteRef = useRef(targetMinute);

  useEffect(() => {
    setSelectedDate(effectiveDate ? parse(effectiveDate, "yyyy-MM-dd", new Date()) : undefined);
  }, [effectiveDate]);

  useEffect(() => {
    const nextMinute = parseTimeOfDay(requestedAt || null) ?? minuteOfDayFromDate(currentTime);
    setTargetMinute(nextMinute);
    lastCommittedMinuteRef.current = nextMinute;
  }, [currentTime, selectedTimeId, effectiveDate, requestedAt]);

  // 带凭证调 history-token，返回 href；needCaptcha=true 时返回 null（需先走验证码）
  const fetchHistoryToken = useCallback(async (
    date: string,
    minute: number,
    extra: { captchaVerifyParam?: string | null; passToken?: string } = {},
  ): Promise<{ href: string; passToken?: string; passExpiresAt?: number } | null> => {
    const authToken = typeof window !== "undefined"
      ? localStorage.getItem("auth_token")
      : null;

    const headers: Record<string, string> = { "Content-Type": "application/json" };
    if (authToken) headers["Authorization"] = `Bearer ${authToken}`;

    const res = await fetch("/api/history-token", {
      method: "POST",
      headers,
      body: JSON.stringify({
        date,
        at: formatMinuteOfDay(minute),
        ...extra,
      }),
    });

    if (res.status === 401) return null; // needCaptcha
    // 验证码已通过、但服务端复核失败（如 localhost 域名未加白）→ 返回 null 走兜底
    if (res.status === 400) {
      const data = await res.json().catch(() => ({}));
      if (data && (data as { captchaVerifyResult?: boolean }).captchaVerifyResult === false) {
        return null;
      }
    }
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }, []);

  const navigateToHistory = useCallback(async (date: string, minute: number) => {
    const timeText = formatMinuteOfDay(minute);
    setMintingToken(true);
    setVerifyError(null);
    setPendingText(`正在定位 ${date} ${timeText} 的热搜现场`);

    try {
      // 已登录：JWT 在 fetchHistoryToken 内自动携带，服务端直接放行
      // 未登录：尝试 passToken，失败则触发验证码
      const currentPass = passToken && Date.now() < passToken.expiresAt ? passToken : null;
      const result = await fetchHistoryToken(
        date,
        minute,
        currentPass ? { passToken: currentPass.token } : {},
      );

      if (!result) {
        if (isDateStringOlderThanLoginRequiredWindow(date)) {
          setMintingToken(false);
          if (user) {
            setVerifyError("登录状态已过期，请重新登录后查看");
            return;
          }
          requireHistoryLogin(() => {
            void navigateToHistory(date, minute);
          });
          return;
        }
        // 服务端返回 needCaptcha（无有效凭证），触发无感验证
        if (CAPTCHA_CONFIGURED) {
          pendingNavigationRef.current = { date, minute };
          setMintingToken(false);
          captchaRef.current?.trigger();
          return;
        }
        // 未配置验证码（dev 环境）：直接重试，服务端 dev 模式放行（verifyCaptcha(null) 返回 true）
        const retry = await fetchHistoryToken(date, minute, { captchaVerifyParam: null });
        if (!retry?.href) {
          setVerifyError("无法验证身份，请稍后重试或登录后查看");
          return;
        }
        trackTimeTravel(`${date} ${timeText}`);
        claritySetTag("history_mode", "history");
        startTransition(() => router.push(retry.href));
        return;
      }

      // 如果服务端签发了新 passToken，保存到内存 + sessionStorage
      if (result.passToken && result.passExpiresAt) {
        const newPass = { token: result.passToken, expiresAt: result.passExpiresAt };
        setPassToken(newPass);
        savePass(newPass);
      }

      if (result.href) {
        trackTimeTravel(`${date} ${timeText}`);
        claritySetTag("history_mode", "history");
        startTransition(() => router.push(result.href));
      }
    } catch (e) {
      console.error("[history] navigateToHistory failed:", e);
      setVerifyError("访问失败，请稍后重试");
    } finally {
      setMintingToken(false);
    }
  }, [passToken, fetchHistoryToken, startTransition, router, requireHistoryLogin, user]);

  // 验证码回调：验证通过后，带 captchaVerifyParam 重新请求
  const handleCaptchaVerify = useCallback(async (
    captchaVerifyParam: string,
  ): Promise<CaptchaVerifyOutcome> => {
    const pending = pendingNavigationRef.current;
    if (!pending) return { captchaResult: true, bizResult: false };

    try {
      const result = await fetchHistoryToken(pending.date, pending.minute, { captchaVerifyParam });
      // result === null 表示验证码服务端复核失败（captchaVerifyResult: false）
      if (result === null) {
        setVerifyError("人机验证未通过，请重试或登录后查看");
        return { captchaResult: false, bizResult: false };
      }

      if (result.passToken && result.passExpiresAt) {
        const newPass = { token: result.passToken, expiresAt: result.passExpiresAt };
        setPassToken(newPass);
        savePass(newPass);
      }

      if (result.href) {
        const timeText = formatMinuteOfDay(pending.minute);
        trackTimeTravel(`${pending.date} ${timeText}`);
        claritySetTag("history_mode", "history");
        pendingNavigationRef.current = null;
        startTransition(() => router.push(result.href));
      }

      return { captchaResult: true, bizResult: true };
    } catch {
      setVerifyError("网络异常，请稍后重试");
      return { captchaResult: true, bizResult: false };
    }
  }, [fetchHistoryToken, startTransition, router]);

  const handleDateChange = async (date: Date | undefined) => {
    if (date) {
      if (isOlderThanLoginRequiredWindow(date)) {
        requireHistoryLogin(() => {
          const formatted = format(date, "yyyy-MM-dd");
          void navigateToHistory(formatted, targetMinute);
        });
        return;
      }
      const formatted = format(date, "yyyy-MM-dd");
      await navigateToHistory(formatted, targetMinute);
    } else {
      setSelectedDate(undefined);
      claritySetTag("history_mode", "realtime");
      router.push("/");
    }
  };

  const handleToday = () => {
    setSelectedDate(undefined);
    setPendingText("正在回到实时热搜");
    startTransition(() => {
      router.push("/");
    });
  };

  const clampMinute = (minute: number) => Math.max(0, Math.min(23 * 60 + 59, minute));

  const [nowMinuteOfDay, setNowMinuteOfDay] = useState<number | null>(null);
  const [dragging, setDragging] = useState(false);
  const [futureNotice, setFutureNotice] = useState("");
  const futureNoticeTimerRef = useRef<number | null>(null);
  useEffect(() => {
    const compute = () => {
      const now = new Date();
      const bjHour = (now.getUTCHours() + 8) % 24;
      setNowMinuteOfDay(bjHour * 60 + now.getUTCMinutes());
    };
    compute();
    const timer = setInterval(compute, 30000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    return () => {
      if (futureNoticeTimerRef.current !== null) {
        window.clearTimeout(futureNoticeTimerRef.current);
      }
    };
  }, []);

  const showFutureNotice = useCallback((message: string, autoHide = true) => {
    setFutureNotice(message);
    if (futureNoticeTimerRef.current !== null) {
      window.clearTimeout(futureNoticeTimerRef.current);
      futureNoticeTimerRef.current = null;
    }
    if (autoHide) {
      futureNoticeTimerRef.current = window.setTimeout(() => {
        setFutureNotice("");
        futureNoticeTimerRef.current = null;
      }, 3200);
    }
  }, []);

  const latestRealtimeMinuteText = nowMinuteOfDay === null ? null : formatMinuteOfDay(nowMinuteOfDay);
  const todayDate = formatDateParam(new Date());
  const activeDate = effectiveDate || todayDate;
  const isViewingToday = activeDate === todayDate;

  const isFutureTodayMinute = useCallback((minute: number) => (
    isViewingToday && nowMinuteOfDay !== null && minute > nowMinuteOfDay
  ), [isViewingToday, nowMinuteOfDay]);

  const handleTodayFutureBoundary = useCallback((message?: string) => {
    const fallbackMinute = lastCommittedMinuteRef.current;
    setTargetMinute(fallbackMinute);
    showFutureNotice(
      message || (
        latestRealtimeMinuteText
          ? `今天数据更新到 ${latestRealtimeMinuteText}；未来时段还没生成，查看完整一天可切换历史日期`
          : "未来时段还没生成，查看完整一天可切换历史日期"
      ),
    );
  }, [latestRealtimeMinuteText, showFutureNotice]);

  const navigateToMinute = (minute: number) => {
    const rawMinute = clampMinute(minute);
    if (isFutureTodayMinute(rawMinute)) {
      handleTodayFutureBoundary(
        latestRealtimeMinuteText
          ? `今天数据更新到 ${latestRealtimeMinuteText}，只能查看已经发生的时间`
          : "只能查看已经发生的时间",
      );
      return;
    }
    lastCommittedMinuteRef.current = rawMinute;
    setTargetMinute(rawMinute);
    void navigateToHistory(activeDate, rawMinute);
  };

  const handleConfirmTime = (minute = targetMinute) => {
    navigateToMinute(minute);
  };

  const selectedTimeText = formatMinuteOfDay(targetMinute);
  const targetHour = Math.floor(targetMinute / 60);
  const targetMinutePart = targetMinute % 60;
  const period = useMemo(() => getTimePeriod(targetMinute), [targetMinute]);
  const scene = useMemo(() => getSceneVisuals(targetMinute), [targetMinute]);

  const thumbPercent = (targetMinute / (24 * 60)) * 100;
  const nowPercent = nowMinuteOfDay === null ? null : (nowMinuteOfDay / (24 * 60)) * 100;
  const isSunDominant = scene.sunOpacity >= scene.moonOpacity;
  const sceneTextShadow = isSunDominant
    ? "0 1px 18px rgba(15,23,42,0.24)"
    : "0 1px 24px rgba(0,0,0,0.46)";
  const sceneTransition = dragging ? "none" : "opacity 260ms ease, left 220ms ease, top 220ms ease, transform 260ms ease, box-shadow 220ms ease";
  const atmosphereTransition = dragging ? "none" : "opacity 320ms ease, transform 320ms ease, background 320ms ease";
  const phaseStops = [
    { minute: 0, label: "00" },
    { minute: 360, label: "06" },
    { minute: 720, label: "12" },
    { minute: 1080, label: "18" },
    { minute: 1439, label: "24" },
  ];

  return (
    <div className="mb-2 sm:mb-4">
      <section className="overflow-hidden rounded-xl border border-slate-200/80 bg-background shadow-sm dark:border-slate-800">
        <div
          className="relative min-h-[114px] overflow-hidden px-3 py-2 text-white sm:min-h-[128px] sm:px-4 sm:py-2.5"
          style={{ background: "linear-gradient(160deg, #020617 0%, #0f172a 52%, #172554 100%)" }}
        >
          <div
            className="pointer-events-none absolute inset-0"
            style={{
              opacity: scene.dawn,
              transition: sceneTransition,
              background: "linear-gradient(160deg, #25305f 0%, #d96a5b 54%, #ffd89a 100%)",
            }}
          />
          <div
            className="pointer-events-none absolute inset-0"
            style={{
              opacity: scene.day,
              transition: sceneTransition,
              background: "linear-gradient(160deg, #7dd3fc 0%, #dbeafe 52%, #fff1b8 100%)",
            }}
          />
          <div
            className="pointer-events-none absolute inset-0"
            style={{
              opacity: scene.noon,
              transition: sceneTransition,
              background: "linear-gradient(160deg, rgba(56,189,248,0.88) 0%, rgba(147,197,253,0.72) 48%, rgba(254,243,199,0.7) 100%)",
            }}
          />
          <div
            className="pointer-events-none absolute inset-0"
            style={{
              opacity: scene.sunset,
              transition: sceneTransition,
              background: "linear-gradient(160deg, #4f46e5 0%, #fb7185 46%, #fbbf24 100%)",
            }}
          />
          <div
            className="pointer-events-none absolute inset-0"
            style={{
              opacity: scene.evening,
              transition: sceneTransition,
              background: "linear-gradient(160deg, #131a3c 0%, #3b2163 50%, #7c2d12 100%)",
            }}
          />
          <div
            className="pointer-events-none absolute inset-0"
            style={{
              opacity: scene.starsOpacity,
              transform: `translate3d(${scene.starShift}px, ${scene.night * 2}px, 0)`,
              transition: atmosphereTransition,
              backgroundImage:
                "radial-gradient(circle at 10% 18%, rgba(255,255,255,0.95) 0 1px, transparent 1.6px), radial-gradient(circle at 28% 34%, rgba(255,255,255,0.62) 0 1px, transparent 1.8px), radial-gradient(circle at 62% 15%, rgba(255,255,255,0.8) 0 1px, transparent 1.7px), radial-gradient(circle at 82% 42%, rgba(255,255,255,0.58) 0 1px, transparent 1.7px)",
            }}
          />
          <div
            className="pointer-events-none absolute inset-0"
            style={{
              opacity: scene.starsOpacity * 0.45,
              transform: `translate3d(${-scene.starShift * 0.7}px, ${-scene.night * 1.5}px, 0)`,
              transition: atmosphereTransition,
              backgroundImage:
                "radial-gradient(circle at 18% 62%, rgba(255,255,255,0.55) 0 1px, transparent 1.8px), radial-gradient(circle at 46% 24%, rgba(255,255,255,0.72) 0 1px, transparent 1.6px), radial-gradient(circle at 72% 68%, rgba(255,255,255,0.5) 0 1px, transparent 1.7px), radial-gradient(circle at 92% 18%, rgba(255,255,255,0.68) 0 1px, transparent 1.7px)",
            }}
          />
          <div
            className="pointer-events-none absolute inset-x-[-8%] top-[-55%] h-[150%]"
            style={{
              opacity: scene.ambientGlow,
              transform: `translate3d(${scene.cloudShift * 0.24}px, 0, 0)`,
              transition: atmosphereTransition,
              background: `radial-gradient(ellipse at ${scene.lightFocus}% 42%, rgba(255,255,255,0.22), rgba(255,255,255,0.07) 34%, rgba(255,255,255,0) 66%)`,
            }}
          />
          <div
            className="pointer-events-none absolute inset-x-0 bottom-0 h-16"
            style={{
              background: `radial-gradient(ellipse at ${scene.lightFocus}% 100%, rgba(251,191,36,${scene.horizonWarmth}), rgba(251,113,133,0.18) 38%, rgba(124,58,237,0.08) 66%, rgba(14,165,233,0) 100%)`,
              transition: atmosphereTransition,
              opacity: clamp(0.7 + scene.sunset * 0.2 + scene.dawn * 0.12 + scene.night * 0.08, 0.7, 0.94),
            }}
          />
          <div
            className="pointer-events-none absolute inset-x-0 bottom-0 h-14"
            style={{
              background:
                "linear-gradient(180deg, rgba(255,255,255,0), rgba(255,255,255,0.16)), radial-gradient(ellipse at 35% 78%, rgba(255,255,255,0.2), transparent 56%), radial-gradient(ellipse at 78% 82%, rgba(255,255,255,0.14), transparent 58%)",
              opacity: scene.cloudOpacity,
              transform: `translate3d(${scene.cloudShift}px, 0, 0)`,
              transition: atmosphereTransition,
            }}
          />
          <div
            className="pointer-events-none absolute -left-14 top-10 h-20 w-64 rounded-full blur-3xl"
            style={{
              backgroundColor: "rgba(255,255,255,0.24)",
              opacity: scene.mistOpacity,
              transform: `translate3d(${scene.cloudShift * 0.55}px, 0, 0)`,
              transition: atmosphereTransition,
            }}
          />
          <div
            className="pointer-events-none absolute -right-12 top-3 h-20 w-72 rounded-full blur-3xl"
            style={{
              backgroundColor: "rgba(147,197,253,0.22)",
              opacity: scene.cloudOpacity * 0.78,
              transform: `translate3d(${scene.cloudShift * -0.4}px, 0, 0)`,
              transition: atmosphereTransition,
            }}
          />
          <div
            className="pointer-events-none absolute h-28 w-28 -translate-x-1/2 -translate-y-1/2 rounded-full blur-xl sm:h-36 sm:w-36"
            style={{
              left: `${scene.sunLeft}%`,
              top: `${scene.sunTop}%`,
              opacity: scene.sunOpacity * (dragging ? 0.8 : 0.62),
              background: "radial-gradient(circle, rgba(255,255,255,0.42) 0%, rgba(253,224,71,0.22) 34%, rgba(251,191,36,0) 70%)",
              transform: `translate3d(-50%, -50%, 0) scale(${dragging ? 1.08 : 1})`,
              transition: sceneTransition,
            }}
          />
          <div
            className="pointer-events-none absolute h-10 w-10 -translate-x-1/2 -translate-y-1/2 rounded-full sm:h-12 sm:w-12"
            style={{
              left: `${scene.sunLeft}%`,
              top: `${scene.sunTop}%`,
              opacity: scene.sunOpacity,
              background: "radial-gradient(circle at 34% 30%, #ffffff 0%, #fff7c2 32%, #fde047 68%, #f59e0b 100%)",
              boxShadow: `0 0 28px 8px rgba(250,204,21,0.58), 0 0 76px 24px rgba(251,191,36,0.38)`,
              transform: `translate3d(-50%, -50%, 0) scale(${dragging ? 1.04 : 1})`,
              transition: sceneTransition,
            }}
            aria-hidden="true"
          />
          <div
            className="pointer-events-none absolute h-24 w-24 -translate-x-1/2 -translate-y-1/2 rounded-full blur-xl sm:h-32 sm:w-32"
            style={{
              left: `${scene.moonLeft}%`,
              top: `${scene.moonTop}%`,
              opacity: scene.moonOpacity * (dragging ? 0.78 : 0.56),
              background: "radial-gradient(circle, rgba(219,234,254,0.38) 0%, rgba(147,197,253,0.18) 38%, rgba(96,165,250,0) 72%)",
              transform: `translate3d(-50%, -50%, 0) scale(${dragging ? 1.06 : 1})`,
              transition: sceneTransition,
            }}
          />
          <div
            className="pointer-events-none absolute h-10 w-10 -translate-x-1/2 -translate-y-1/2 rounded-full sm:h-12 sm:w-12"
            style={{
              left: `${scene.moonLeft}%`,
              top: `${scene.moonTop}%`,
              opacity: scene.moonOpacity,
              background: "radial-gradient(circle at 35% 30%, #ffffff 0%, #eef2ff 34%, #c7d2fe 67%, #94a3b8 100%), radial-gradient(circle at 62% 34%, rgba(100,116,139,0.28) 0 7%, transparent 8%), radial-gradient(circle at 46% 66%, rgba(100,116,139,0.22) 0 6%, transparent 7%), radial-gradient(circle at 68% 70%, rgba(255,255,255,0.2) 0 5%, transparent 6%)",
              boxShadow: `0 0 24px 6px rgba(147,197,253,0.52), inset -9px -7px 16px rgba(15,23,42,0.24), inset 6px 5px 12px rgba(255,255,255,0.34)`,
              transform: `translate3d(-50%, -50%, 0) scale(${dragging ? 1.04 : 1})`,
              transition: sceneTransition,
            }}
            aria-hidden="true"
          />
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-white/30" />

          {navigating && (
            <div className="absolute inset-0 z-50 flex items-center justify-center bg-slate-950/40">
              <div className="flex items-center gap-2 rounded-full border border-white/20 bg-slate-950/80 px-4 py-2 text-sm font-medium text-white shadow-sm">
                <Loader2 className="h-4 w-4 animate-spin text-primary" />
                {pendingText || "正在定位历史快照"}
              </div>
            </div>
          )}

          {verifyError && !navigating && (
            <div className="relative z-40 mb-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-900/50 dark:bg-amber-950/40 dark:text-amber-200">
              {verifyError}
              <button
                type="button"
                onClick={() => setVerifyError(null)}
                className="ml-2 underline hover:no-underline"
              >
                关闭
              </button>
            </div>
          )}

          <div className="relative z-30 flex items-start justify-between gap-2">
            <div className="flex min-w-0 items-center gap-1.5 rounded-full bg-black/10 px-2 py-1 shadow-[inset_0_1px_0_rgba(255,255,255,0.14)]">
              <Calendar className="h-3.5 w-3.5 shrink-0 text-white/80" />
              <span className="truncate text-[10px] font-medium text-white/80 sm:text-[11px]">
                {formatSnapshotText(currentTime, selectedDate)}
              </span>
              <span
                className="shrink-0 rounded-full px-1.5 py-px text-[10px] font-semibold text-white"
                style={{ backgroundColor: effectiveDate ? "rgba(251,191,36,0.32)" : "rgba(16,185,129,0.3)" }}
              >
                {effectiveDate ? "历史" : "实时"}
              </span>
            </div>
            <div className="relative z-40 flex shrink-0 items-center gap-0.5 rounded-lg border border-white/20 bg-white/10 p-0.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.16)]">
              {effectiveDate && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleToday}
                  className="h-7 gap-1 border-transparent bg-transparent px-2 text-[11px] text-white hover:bg-white/10 sm:h-8 sm:text-xs"
                  disabled={navigating}
                >
                  <RotateCcw className="h-3 w-3" />
                  现在
                </Button>
              )}
              <DatePicker
                date={selectedDate}
                onDateChange={handleDateChange}
                placeholder="查看历史"
                fromDate={minDate}
                toDate={maxDate || new Date()}
                disabled={navigating}
                variant="scene"
              />
              <button
                type="button"
                onClick={() => setShowMore((v) => !v)}
                aria-expanded={showMore}
                aria-label={showMore ? "收起精确选择" : "展开精确选择"}
                className="inline-flex h-7 items-center gap-0.5 rounded-md border border-transparent bg-transparent px-2 text-[11px] font-medium text-white transition-colors hover:bg-white/10 sm:h-8 sm:px-2.5 sm:text-xs"
              >
                {showMore ? "收起" : "精确"}
                <ChevronDown className={`h-3 w-3 transition-transform ${showMore ? "rotate-180" : ""}`} />
              </button>
            </div>
          </div>

          <div className="relative z-30 mt-2 flex items-end justify-between gap-3 sm:mt-3">
            <div className="min-w-0">
              <div
                className="text-[1.65rem] font-black leading-none tracking-normal sm:text-3xl"
                style={{ textShadow: sceneTextShadow }}
              >
                {selectedTimeText}
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px] text-white/80 sm:text-xs">
                <span
                  className="rounded-full px-2 py-0.5 font-semibold text-white shadow-sm"
                  style={{ backgroundColor: `${period.accent}66` }}
                >
                  {period.label}
                </span>
                <span className="font-medium text-white/80">{period.cue}</span>
              </div>
            </div>
          </div>

          <div
            className="relative z-30 mt-1.5 rounded-xl border border-white/20 bg-black/20 px-2.5 py-1 shadow-[inset_0_1px_0_rgba(255,255,255,0.16),0_12px_30px_rgba(0,0,0,0.14)] transition-shadow sm:mt-2 sm:px-3"
            style={{
              boxShadow: dragging
                ? `inset 0 1px 0 rgba(255,255,255,0.2), 0 14px 34px rgba(0,0,0,0.18), 0 0 0 1px rgba(255,255,255,0.08), 0 0 22px ${period.accent}55`
                : undefined,
            }}
          >
            <div className="pointer-events-none flex items-center justify-between gap-3 text-[10px] font-medium text-white/70 sm:text-xs">
              <span>拖动时间</span>
              <span className="tabular-nums text-white/85">{selectedTimeText}</span>
            </div>
            <div className="pointer-events-none relative mt-1 h-6">
              <div className="absolute inset-x-0 top-1/2 h-1.5 -translate-y-1/2 overflow-hidden rounded-full border border-white/20 bg-black/20">
                <div
                  className="h-full rounded-full"
                  style={{
                    width: `${thumbPercent}%`,
                    background: `linear-gradient(90deg, rgba(255,255,255,0.32), ${period.accent})`,
                    boxShadow: dragging ? `0 0 18px ${period.accent}99` : undefined,
                  }}
                />
              </div>
              {phaseStops.map((mark) => (
                <div
                  key={mark.label}
                  className="absolute top-0 -translate-x-1/2 text-[9px] font-semibold tabular-nums text-white/50"
                  style={{ left: `${(mark.minute / (24 * 60)) * 100}%` }}
                >
                  {mark.label}
                </div>
              ))}
              {nowPercent !== null && (
                <div
                  className="absolute top-2 h-4 -translate-x-1/2"
                  style={{ left: `${nowPercent}%` }}
                >
                  <div className="mx-auto h-4 w-px bg-white/60" />
                  <div className="mt-0.5 h-1 w-1 rounded-full bg-white shadow-[0_0_12px_rgba(255,255,255,0.9)]" />
                </div>
              )}
              <div
                className={`absolute top-1/2 flex h-[22px] w-9 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-white bg-white text-slate-950 shadow-[0_10px_22px_rgba(0,0,0,0.24),0_0_18px_rgba(255,255,255,0.58)] ${
                  dragging ? "scale-125" : "transition-[left,transform] duration-150"
                }`}
                style={{
                  left: `${thumbPercent}%`,
                  boxShadow: dragging
                    ? `0 14px 32px rgba(0,0,0,0.3), 0 0 0 5px rgba(255,255,255,0.16), 0 0 26px ${period.accent}aa`
                    : undefined,
                }}
                aria-hidden="true"
              >
                <span className="mr-1 h-2.5 w-px rounded-full bg-slate-400" />
                <span className="h-3.5 w-px rounded-full bg-slate-500" />
                <span className="ml-1 h-2.5 w-px rounded-full bg-slate-400" />
              </div>
            </div>
            {futureNotice && (
              <div
                className="pointer-events-none mt-1 rounded-lg border border-white/15 bg-white/12 px-2 py-1 text-[10px] font-medium text-white/86 shadow-sm sm:text-xs"
                aria-live="polite"
                role="status"
              >
                {futureNotice}
              </div>
            )}
            <input
              type="range"
              min={0}
              max={23 * 60 + 59}
              step={1}
              value={targetMinute}
              onChange={(event) => {
                const nextMinute = clampMinute(Number(event.target.value));
                setTargetMinute(nextMinute);
                if (isFutureTodayMinute(nextMinute) && latestRealtimeMinuteText) {
                  showFutureNotice(`今天数据更新到 ${latestRealtimeMinuteText}，松手后回到当前快照`, false);
                } else {
                  setFutureNotice("");
                }
              }}
              onPointerDown={() => setDragging(true)}
              onPointerUp={(event) => {
                setDragging(false);
                const rawMinute = clampMinute(Number(event.currentTarget.value));
                if (isFutureTodayMinute(rawMinute)) {
                  handleTodayFutureBoundary(
                    latestRealtimeMinuteText
                      ? `今天数据更新到 ${latestRealtimeMinuteText}；未来时段还没生成，查看完整一天可切换历史日期`
                      : "未来时段还没生成，查看完整一天可切换历史日期",
                  );
                  return;
                }
                handleConfirmTime(rawMinute);
              }}
              onPointerCancel={(event) => {
                setDragging(false);
                const rawMinute = clampMinute(Number(event.currentTarget.value));
                if (isFutureTodayMinute(rawMinute)) {
                  handleTodayFutureBoundary();
                } else {
                  setTargetMinute(lastCommittedMinuteRef.current);
                }
              }}
              onKeyUp={(event) => {
                if (event.key === "Enter") {
                  const rawMinute = clampMinute(Number(event.currentTarget.value));
                  if (isFutureTodayMinute(rawMinute)) {
                    handleTodayFutureBoundary(
                      latestRealtimeMinuteText
                        ? `今天数据更新到 ${latestRealtimeMinuteText}；未来时段还没生成，查看完整一天可切换历史日期`
                        : "未来时段还没生成，查看完整一天可切换历史日期",
                    );
                    return;
                  }
                  handleConfirmTime(rawMinute);
                }
              }}
              disabled={navigating}
              className="absolute inset-0 z-10 h-full w-full cursor-ew-resize opacity-0"
              aria-label="选择时间"
            />
          </div>
        </div>

        {/* 精确选择 + 快捷按钮 — 折叠 */}
        {showMore && (
          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-800 bg-slate-950 px-3 py-3 text-white sm:px-5">
            <div className="flex items-center gap-1 rounded-xl border border-white/10 bg-white/10 p-1">
              <select
                value={targetHour}
                onChange={(event) => navigateToMinute(Number(event.target.value) * 60 + targetMinutePart)}
                disabled={navigating}
                className="h-8 rounded-lg border border-white/10 bg-slate-950 px-2 text-sm font-semibold tabular-nums text-white outline-none focus:ring-1 focus:ring-white/40"
                aria-label="选择小时"
              >
                {Array.from({ length: 24 }, (_, hour) => (
                  <option key={hour} value={hour}>{String(hour).padStart(2, "0")}时</option>
                ))}
              </select>
              <span className="text-sm font-bold text-muted-foreground">:</span>
              <select
                value={Math.floor(targetMinutePart / 5) * 5}
                onChange={(event) => navigateToMinute(targetHour * 60 + Number(event.target.value))}
                disabled={navigating}
                className="h-8 rounded-lg border border-white/10 bg-slate-950 px-2 text-sm font-semibold tabular-nums text-white outline-none focus:ring-1 focus:ring-white/40"
                aria-label="选择分钟"
              >
                {Array.from({ length: 12 }, (_, index) => index * 5).map((minute) => (
                  <option key={minute} value={minute}>{String(minute).padStart(2, "0")}分</option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-1">
              {[
                { label: "-1h", value: -60 },
                { label: "-10m", value: -10 },
                { label: "+10m", value: 10 },
                { label: "+1h", value: 60 },
              ].map((item) => (
                <button
                  key={item.label}
                  type="button"
                  onClick={() => navigateToMinute(targetMinute + item.value)}
                  disabled={navigating}
                  className="h-7 rounded-md border border-white/10 bg-white/10 px-2 text-[11px] font-medium text-white/70 transition-colors hover:bg-white/20 hover:text-white disabled:opacity-40"
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </section>

      {historyLoginDialog}

      {/* 无感验证码：挂载后由 captchaRef.trigger() 驱动，用户无需手动操作 */}
      {CAPTCHA_CONFIGURED && (
        <AliyunCaptcha
          ref={captchaRef}
          sceneId={CAPTCHA_SCENE_ID!}
          prefix={CAPTCHA_PREFIX!}
          onVerify={handleCaptchaVerify}
          onBizSuccess={() => { /* 导航在 handleCaptchaVerify 内部完成 */ }}
          onBizError={() => { /* 验证通过但导航失败，静默忽略，用户可重试 */ }}
        />
      )}
    </div>
  );
}
